import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, Divider, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { Draghandle } from '@/components/lists/DragAndDropList/Draghandle';
import { SortableList } from '@/components/lists/DragAndDropList/SortableList';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import { PresentationPageKind } from '@/types/schema';

import { SubItemsList } from './BundlePageSubItems';

export interface BundlePageSubItem {
  /** Key used to map this sub-item to a config flag. */
  configKey: string;
  label: string;
  checked: boolean;
  /**
   * When set, this sub-item is an auxiliary toggle (e.g. a display option)
   * that is disabled while the sibling sub-item with this configKey is
   * unchecked. Auxiliary sub-items don't gate page inclusion, so they're
   * excluded from the parent row's select-all and checked/indeterminate state.
   */
  dependsOn?: string;
  /**
   * When true, this sub-item is a display option (e.g. hiding $0 line items)
   * that has no sibling dependency. Like a sub-item with `dependsOn`, it is
   * excluded from the parent row's select-all and checked/indeterminate state.
   */
  isDisplayOption?: boolean;
}

/**
 * True for a sub-item that decides whether the page has content. Display
 * options, with or without a dependency, do not gate page inclusion.
 */
export function isContentSubItem(
  subItem: BundlePageSubItem | undefined
): boolean {
  return !subItem?.dependsOn && !subItem?.isDisplayOption;
}

/**
 * Generic page item for the bundle page reorder list.
 * Domain-specific bundle modals can extend this with additional fields
 * (e.g. configKey for mapping to boolean flags).
 */
export interface BundlePageItem {
  /** Stable ID for dnd-kit */
  id: string;
  pageKind: PresentationPageKind;
  label: string;
  checked: boolean;
  /** When true, the page cannot be unchecked. */
  alwaysIncluded?: boolean;
  /** Optional nested sub-items rendered as indented checkboxes below this page. */
  subItems?: BundlePageSubItem[];
}

/**
 * Minimal form shape that the reorder list requires.
 * Any form using BundlePageReorderList must include `pages: BundlePageItem[]`.
 */
interface BundlePagesFormSlice {
  pages: BundlePageItem[];
}

/**
 * Builds page items from a set of definitions, a config record, and a saved page order.
 * Shared helper for any bundle config modal that uses page selection + reordering.
 *
 * @param definitions - The page definitions for the bundle type
 * @param config - Record mapping configKey to boolean. Individual nullish values default
 *                 to true. If the entire config is null/undefined, all pages default to unchecked.
 * @param savedPageKinds - Ordered list of PresentationPageKind from the bundle's
 *                         existing pages array, used to restore saved ordering.
 */
export function buildPageItems<
  T extends BundlePageItem & { configKey: string | null },
>(
  definitions: Omit<T, 'checked'>[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- config objects carry extra fields like __typename and id that we ignore; we only read configKey values
  config: Record<string, any> | null | undefined,
  savedPageKinds: PresentationPageKind[]
): T[] {
  const savedPageOrder = new Map<PresentationPageKind, number>();
  savedPageKinds.forEach((kind, index) => {
    savedPageOrder.set(kind, index);
  });

  const items = definitions.map(
    (def) =>
      ({
        ...def,
        // Always-included pages are always checked regardless of config.
        // When config exists, individual nullish flags default to true (matching
        // the ?? true normalization used in getDefaultValues). When config is
        // entirely absent, all pages default to unchecked. Items with a null
        // configKey (e.g. alwaysIncluded waterfall pages) default to true.
        checked: def.alwaysIncluded
          ? true
          : config
            ? def.configKey
              ? (config[def.configKey] ?? true)
              : true
            : false,
      }) as T
  );

  // Sort: checked items by their saved page order, unchecked items at the end in default order
  items.sort((a, b) => {
    const aOrder = savedPageOrder.get(a.pageKind);
    const bOrder = savedPageOrder.get(b.pageKind);

    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }
    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;
    return 0;
  });

  return items;
}

interface SortablePageRowProps {
  id: string;
  index: number;
  label: string;
  alwaysIncluded?: boolean;
  subItems?: BundlePageSubItem[];
}

function SortablePageRow({
  id,
  index,
  label,
  alwaysIncluded,
  subItems,
}: SortablePageRowProps) {
  const { control, setValue } = useFormContext<BundlePagesFormSlice>();
  const checked = useWatch({
    control,
    name: `pages.${index}.checked`,
  });

  // For pages with sub-items, derive checkbox state from children
  const watchedSubItems = useWatch({
    control,
    name: `pages.${index}.subItems` as 'pages.0.subItems',
  });

  const hasSubItems = subItems && subItems.length > 0;

  const { allChecked, someChecked } = useMemo(() => {
    if (!hasSubItems || !watchedSubItems) {
      return { allChecked: checked, someChecked: false };
    }
    // Display options (e.g. "Include details on summary tiles") don't gate
    // page inclusion, so the parent's checked/indeterminate state derives only
    // from the content sub-items.
    const contentSubItems = watchedSubItems.filter(isContentSubItem);
    const checkedCount = contentSubItems.filter((s) => s?.checked).length;
    return {
      allChecked:
        contentSubItems.length > 0 && checkedCount === contentSubItems.length,
      someChecked: checkedCount > 0 && checkedCount < contentSubItems.length,
    };
  }, [hasSubItems, watchedSubItems, checked]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !checked });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    position: 'relative' as const,
    zIndex: isDragging ? 1 : 0,
  };

  const handleToggle = useCallback(() => {
    if (alwaysIncluded) return;

    if (hasSubItems) {
      // Select-all behavior: toggle all content children to the new state.
      // Display options are independent, so select-all leaves their value
      // untouched.
      const newChecked = !allChecked;
      setValue(`pages.${index}.checked`, newChecked);
      subItems.forEach((subItem, subIndex) => {
        if (!isContentSubItem(subItem)) return;
        setValue(
          `pages.${index}.subItems.${subIndex}.checked` as `pages.${number}.checked`,
          newChecked
        );
      });
    } else {
      setValue(`pages.${index}.checked`, !checked);
    }
  }, [
    setValue,
    index,
    checked,
    alwaysIncluded,
    hasSubItems,
    allChecked,
    subItems,
  ]);

  // For pages with sub-items, the visual checked state is derived from children
  const isChecked = hasSubItems ? allChecked : checked;
  const isIndeterminate = hasSubItems ? someChecked : false;

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      borderBottom={`1px solid ${COLORS.GRAY[200]}`}
      sx={{
        backgroundColor: isDragging ? COLORS.GRAY[50] : 'transparent',
        borderRadius: isDragging ? 1 : 0,
        '&:first-of-type': {
          borderTop: `1px solid ${COLORS.GRAY[200]}`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} py={1} px={0.5}>
        <Draghandle
          {...(checked ? { ...listeners, ...attributes } : {})}
          sx={{
            cursor: checked ? 'grab' : 'default',
            opacity: checked ? 1 : 0.4,
          }}
        />
        <Typography
          variant="body-s"
          sx={{
            flex: 1,
            userSelect: 'none',
            cursor: 'pointer',
            color: checked ? undefined : COLORS.GRAY[300],
          }}
          onClick={handleToggle}
        >
          {label}
        </Typography>
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={handleToggle}
          size="small"
          disabled={alwaysIncluded}
        />
      </Stack>
      {hasSubItems && (
        <>
          <Divider sx={{ borderColor: COLORS.GRAY[200] }} />
          <SubItemsList pageIndex={index} subItems={subItems} />
        </>
      )}
    </Stack>
  );
}

/**
 * Drag-and-drop reorderable list of bundle pages with checkboxes.
 * Must be rendered inside a FormProvider whose shape includes `pages: BundlePageItem[]`.
 */
export function BundlePageReorderList() {
  const { control } = useFormContext<BundlePagesFormSlice>();

  const { fields, move } = useFieldArray({
    control,
    name: 'pages',
    keyName: '_rhfId',
  });

  return (
    <SortableList
      items={fields}
      getId={(field) => field._rhfId}
      onReorder={move}
    >
      <Stack direction="column">
        {fields.map((field, index) => (
          <SortablePageRow
            key={field._rhfId}
            id={field._rhfId}
            index={index}
            label={field.label}
            alwaysIncluded={field.alwaysIncluded}
            subItems={field.subItems}
          />
        ))}
      </Stack>
    </SortableList>
  );
}
